import { matchPath } from 'react-router-dom';

import { OWNERS_HUB_ROUTES } from '@/constants/routes';

/**
 * Resolves the `redirect_url` the auth service now returns when a magic-link token is
 * redeemed (`GET {userApiUrl}/auth/link/<token>`), into an in-app path the router can
 * navigate to.
 *
 * Sample response field (support impersonation link, spark-test):
 *   "redirect_url": "https://owners-hub.spark-test.newtoncelestial.com/account/home"
 *
 * The backend hands back an ABSOLUTE url built from the deployed host it knows about, but
 * the SPA is served from several origins (the deployed host, `127.0.0.1:5173` in dev, and
 * `capacitor://localhost` in the native shell). So we deliberately keep only the
 * path/query/hash and navigate IN-APP, whatever host the value carries:
 *
 *  - it keeps the feature working in dev and inside the native app, where a cross-origin
 *    jump would either bounce the developer onto the test deployment or eject the owner
 *    out of the app into Safari, in both cases landing on an origin that does not hold the
 *    token we just stored;
 *  - and it means a tampered link can never turn this into an open redirect: the
 *    destination is always this origin, so a hostile host in `redirect_url` is discarded
 *    rather than followed.
 *
 * Returns `null` when there is nothing usable to honour, and the caller falls back to the
 * regular brand-state routing ({@link ./post-auth-route}).
 */

/** Where a bare path is resolved against. Guarded so the (DOM-less) jest env can import this. */
const readOrigin = (): string =>
  typeof window !== 'undefined' && typeof window.location?.origin === 'string'
    ? window.location.origin
    : 'http://localhost';

// Sending a freshly authenticated owner back to the sign-in / sign-up screen is never a
// destination, it just parks them on a form they no longer need (and reads as a login loop).
// Such a value falls back to the normal post-auth route instead.
const AUTH_SCREENS = [OWNERS_HUB_ROUTES.LOGIN, OWNERS_HUB_ROUTES.REGISTER];

/** The magic-link token parameter. Whoever holds it can be signed in AS that identity. */
const MAGIC_LINK_PARAM = 'link';

/**
 * Decode a path the way the ROUTER does before matching it.
 *
 * `matchRoutes` (what `createBrowserRouter` runs) percent-decodes the pathname first, so `/%6cogin`
 * is matched against `/login` and renders the sign-in page. `matchPath` on its own does NOT decode,
 * which is precisely how a hand-rolled guard drifts out of step with real routing: comparing the
 * raw pathname let every percent-encoded spelling of an auth path through while the app still
 * routed it to the auth screen.
 *
 * A malformed escape (`/%`, `/%zz`) makes `decodeURIComponent` throw; the router's own decode
 * throws there too and treats the location as unmatched, so falling back to the raw value keeps us
 * aligned with it rather than guessing.
 */
const decodeLikeRouter = (pathname: string): string => {
  try {
    return decodeURIComponent(pathname);
  } catch {
    return pathname;
  }
};

export const resolveLoginRedirect = (
  redirectUrl: string | null | undefined,
  origin: string = readOrigin(),
): string | null => {
  if (typeof redirectUrl !== 'string') {
    return null;
  }

  const raw = redirectUrl.trim();
  if (raw === '') {
    return null;
  }

  let parsed: URL;
  try {
    // A relative value ("/account/players") resolves against our own origin; an absolute one
    // keeps its own host, which the pathname extraction below then drops.
    parsed = new URL(raw, origin);
  } catch {
    return null;
  }

  // `javascript:` and `data:` values parse as perfectly valid URLs, so the scheme has to be
  // checked explicitly before anything derived from them reaches the router.
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return null;
  }

  // COLLAPSE LEADING SLASHES BEFORE ANYTHING ELSE LOOKS AT THE PATH. `new URL` keeps a doubled
  // slash in the path, so `https://<host>//evil.tld/x` parses with `pathname === '//evil.tld/x'`
  // (so does `https://<host>/..//evil.tld`, which normalises to the same thing). A value starting
  // with `//` is PROTOCOL-RELATIVE: handed to `navigate()` it resolves against the current scheme
  // and leaves this origin entirely — `history.pushState` throws cross-origin and the router's
  // catch falls back to `window.location.assign` (@remix-run/router push()), a real full-page
  // navigation off the app immediately after a successful sign-in. The `startsWith('/')` guard
  // below reads as "same-origin path" but accepts `//`, so on its own it did NOT deliver the
  // same-origin promise this module documents.
  //
  // Collapse rather than reject, because the same shape is what a plain base+path concatenation
  // bug on the auth service produces: `https://<host>//account/home` becomes the intended
  // `/account/home`, while a hostile `//evil.tld/x` becomes the inert same-origin `/evil.tld/x`.
  // Only the LEADING run is collapsed; an interior `//` is a normal (if odd) path.
  const pathname = parsed.pathname.replace(/^\/+/, '/');

  // NEVER carry a magic-link token into the destination, whatever the path.
  //
  // The auth-screen check below is an allowlist of two literal paths, and it cannot be complete:
  // the legacy `module.owners.hub.login` is still registered in the component registry, and
  // config-driven pages are mounted at whatever paths the REMOTE templatization config supplies,
  // so a second `?link=` redemption surface can appear at a path this file has never heard of,
  // with no code change here. Since a `link` param is a credential, and the redemption effect
  // re-fires whenever it changes, a destination carrying one could sign the owner in as whoever
  // minted it. Strip it at the source: no legitimate post-auth destination needs it, because the
  // link has already been redeemed by the time this value is resolved.
  // Rebuild the query ONLY when a token is actually there. `searchParams.toString()` re-encodes
  // everything it round-trips (`?q=a%20b` comes back as `?q=a+b`), which parses identically but
  // is a needless rewrite of every other destination, so leave an untainted query byte-for-byte.
  let search = parsed.search;
  if (parsed.searchParams.has(MAGIC_LINK_PARAM)) {
    parsed.searchParams.delete(MAGIC_LINK_PARAM);
    const rebuilt = parsed.searchParams.toString();
    search = rebuilt === '' ? '' : `?${rebuilt}`;
  }

  const target = `${pathname}${search}${parsed.hash}`;

  // `new URL` always yields an absolute pathname for an http(s) url; the guard is the
  // contract this function promises its callers (a value they can hand straight to navigate).
  if (!target.startsWith('/')) {
    return null;
  }

  // Ask the ROUTER whether this lands on an auth screen, on the same normalisation the router
  // itself applies. Do NOT hand-roll the comparison.
  //
  // Every bypass this guard has had came from normalising differently than routing does. A raw
  // `includes()` missed `<origin>//login` (pathname `//login`, fixed by the collapse above). A
  // case-sensitive compare missed `/LOGIN`, because matchers are compiled
  // `new RegExp(source, caseSensitive ? undefined : 'i')` and no route opts in. And a lowercased
  // string compare still missed `/%6cogin`, because `matchRoutes` percent-DECODES the pathname
  // before matching, so every encoded spelling routed to the auth screen while the guard saw a
  // stranger. `matchPath` alone would not have caught that either: it does not decode.
  //
  // Decoding first and then matching with the router's own matcher covers all three axes at once
  // (case, trailing slash, encoding) and stays correct if the matcher's rules change.
  const decoded = decodeLikeRouter(pathname);
  if (AUTH_SCREENS.some((screen) => matchPath(screen, decoded) != null)) {
    return null;
  }

  return target;
};

export default resolveLoginRedirect;
